/**
 * Import d'un export **SafeInCloud** XML (« Fichier > Exporter > XML » de
 * SafeInCloud, Windows / macOS / Android / iOS : ses utilisateurs doivent
 * pouvoir partir avec TOUT leur coffre).
 *
 * Purement local, aucune E/S reseau : ce module transforme un export en
 * `ImportedEntry`. Le document `<database>` porte des `label` (`id`, `name`)
 * et des `card` (`title`, `type="note"` pour une note, `star`, `template`,
 * `deleted`). Une fiche porte ses `field` (`name`, `type`, `autofill`, valeur
 * en texte), ses `notes`, ses `label_id`, ses fichiers `file` (`name`, base64
 * compresse zlib) et ses images `image` (base64). Les modeles et la corbeille
 * sont ignores ; une fiche a numero de carte (`autofill="cc-number"`) devient
 * une carte ; les etiquettes deviennent des etiquettes (une fiche peut en
 * porter plusieurs). Tout champ non repris devient un champ personnalise,
 * masque si son type ou son libelle designe un secret.
 */
import { base64ToBytes, type EntryCustomField } from '@klavox/crypto-core';
import { csvColumnField, joinNotes, normalizeTotpSecret, type ImportedAttachment, type ImportedEntry } from './csv-import.js';
import { childNamed, childrenNamed, parseXml, type XmlNode } from './kdbx-import.js';
import { normalizeExpiry, normalizeTypedFields, serverHost } from './typed-fields.js';
import { decompress } from './zip.js';

/** Types de champ SafeInCloud dont la valeur est un secret : masques s'ils restent en champ. */
const SECRET_TYPES = new Set(['password', 'secret', 'pin', 'one_time_password']);

/** Libelle du mot de passe principal quand une fiche en porte plusieurs (SafeInCloud localise ses modeles). */
const PASSWORD_NAME = /^(?:password|mot de passe|passwort|contrase[nñ]a|senha|wachtwoord|parola d'ordine|пароль)$/iu;

/** Libelle de la banque emettrice d'une carte (modele « Carte de credit » localise). */
const BANK_NAME = /^(?:bank|bank name|banque|banque emettrice|bank\s*\/\s*issuer|issuer|banco|bankname)$/iu;

interface SafeInCloudField {
  readonly name: string;
  readonly type: string;
  readonly autofill: string;
  /** Valeur brute, jamais rognee : une espace peut faire partie d'un mot de passe. */
  readonly value: string;
}

/** Le document SafeInCloud, ou `null` si le texte n'en est pas un. */
function safeInCloudDatabase(text: string): XmlNode | null {
  if (!/^[\s﻿]*(?:<\?xml[^>]*>\s*)?(?:<!--[\s\S]*?-->\s*)*<database[\s>]/u.test(text)) return null;
  let document: XmlNode;
  try {
    document = parseXml(text);
  } catch {
    return null;
  }
  const database = childNamed(document, 'database');
  if (database === null) return null;
  return childNamed(database, 'card') !== null || childNamed(database, 'label') !== null ? database : null;
}

function fieldsOf(card: XmlNode): SafeInCloudField[] {
  return childrenNamed(card, 'field')
    .map((field) => ({
      name: (field.attributes['name'] ?? '').trim(),
      type: (field.attributes['type'] ?? '').trim().toLowerCase(),
      autofill: (field.attributes['autofill'] ?? '').trim().toLowerCase(),
      value: field.text,
    }))
    .filter((field) => field.value.trim().length > 0);
}

/** Champ garde tel quel : masque si son type ou son libelle designe un secret. */
function keptField(field: SafeInCloudField): EntryCustomField {
  const label = field.name || field.type || 'Champ';
  const value = SECRET_TYPES.has(field.type) ? field.value : field.value.trim();
  const custom = csvColumnField(label, value);
  return SECRET_TYPES.has(field.type) ? { ...custom, hidden: true } : custom;
}

/** Extension d'une image, lue a sa signature (SafeInCloud ne la nomme pas). */
function imageExtension(bytes: Uint8Array): string {
  if (bytes[0] === 0xff && bytes[1] === 0xd8) return 'jpg';
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return 'png';
  if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) return 'gif';
  if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46) return 'webp';
  return 'bin';
}

/** Fichiers (base64 + zlib) et images (base64) d'une fiche ; une piece illisible est nommee dans les notes, jamais perdue en silence. */
async function safeInCloudAttachments(card: XmlNode): Promise<{ attachments: ImportedAttachment[]; missing: string[] }> {
  const attachments: ImportedAttachment[] = [];
  const missing: string[] = [];
  for (const file of childrenNamed(card, 'file')) {
    const fileName = (file.attributes['name'] ?? '').trim() || 'piece-jointe';
    try {
      const bytes = await decompress(base64ToBytes(file.text.replace(/\s+/gu, '')), 'deflate');
      if (bytes.length > 0) {
        attachments.push({ fileName, bytes });
        continue;
      }
    } catch {
      // illisible : nommee dans les notes ci-dessous
    }
    missing.push(fileName);
  }
  for (const [index, image] of childrenNamed(card, 'image').entries()) {
    let bytes: Uint8Array | null = null;
    try {
      bytes = base64ToBytes(image.text.replace(/\s+/gu, ''));
    } catch {
      bytes = null;
    }
    const baseName = `Image ${index + 1}`;
    if (bytes !== null && bytes.length > 0) attachments.push({ fileName: `${baseName}.${imageExtension(bytes)}`, bytes });
    else missing.push(baseName);
  }
  return { attachments, missing };
}

/**
 * Date du mot de passe : SafeInCloud ne date que la fiche ; `time_stamp`
 * (millisecondes, derniere modification) = borne haute, jamais `prev_stamp`
 * (modification precedente). L'`history` des champs n'est pas lu : son sens
 * (date de pose ou de remplacement) n'est pas documente. Aucune date sans mot
 * de passe ; `0`, absente ou illisible : l'import vaut pose.
 */
function safeInCloudPasswordChangedAt(card: XmlNode, password: string): { passwordChangedAt?: number } {
  const stamp = password.length > 0 ? Number(card.attributes['time_stamp']) : NaN;
  return Number.isFinite(stamp) && stamp > 0 ? { passwordChangedAt: stamp } : {};
}

/** Une fiche -> une entree (identifiant, carte ou note). */
async function safeInCloudEntry(card: XmlNode, labels: ReadonlyMap<string, string>): Promise<ImportedEntry> {
  const fields = fieldsOf(card);
  const title = (card.attributes['title'] ?? '').trim();
  const noteParts = childrenNamed(card, 'notes').map((notes) => notes.text.trim());
  const customFields: EntryCustomField[] = [];
  const tags = [
    ...new Set(
      childrenNamed(card, 'label_id')
        .map((labelId) => labels.get(labelId.text.trim()) ?? '')
        .filter((name) => name.length > 0),
    ),
  ];
  const { attachments, missing } = await safeInCloudAttachments(card);
  const isCard = card.attributes['type'] !== 'note' && fields.some((field) => field.autofill === 'cc-number');
  const cardFields: Record<string, string> = {};
  let username = '';
  let password = '';
  let totp = { secret: '', extraNote: '' };
  const uris: string[] = [];

  if (card.attributes['type'] === 'note') {
    for (const field of fields) {
      if (field.type === 'notes') noteParts.push(field.value.trim());
      else customFields.push(keptField(field));
    }
  } else if (isCard) {
    for (const field of fields) {
      const value = field.value.trim();
      if (field.autofill === 'cc-number' && cardFields['number'] === undefined) cardFields['number'] = value;
      else if (field.autofill === 'cc-name' && cardFields['cardholder'] === undefined) cardFields['cardholder'] = value;
      else if (field.autofill === 'cc-csc' && cardFields['cvv'] === undefined) cardFields['cvv'] = value;
      else if (field.autofill === 'cc-exp' && cardFields['expiry'] === undefined && normalizeExpiry(value) !== null) {
        cardFields['expiry'] = normalizeExpiry(value)!;
      } else if (field.type === 'pin' && cardFields['pin'] === undefined) cardFields['pin'] = value;
      else if (BANK_NAME.test(field.name) && cardFields['bank'] === undefined) cardFields['bank'] = value;
      else if (field.type === 'notes') noteParts.push(value);
      else customFields.push(keptField(field));
    }
  } else {
    // SafeInCloud admet plusieurs mots de passe : celui nomme « Password » (ou le premier) est le principal.
    const candidates = fields.filter((field) => field.type === 'password' || field.type === 'secret');
    const main = candidates.find((field) => PASSWORD_NAME.test(field.name)) ?? candidates[0];
    const usernameField = fields.find((field) => field.type === 'login') ?? fields.find((field) => field.type === 'email');
    const totpField = fields.find((field) => field.type === 'one_time_password');
    for (const field of fields) {
      if (field === main) password = field.value;
      else if (field === usernameField) username = field.value.trim();
      else if (field === totpField) totp = normalizeTotpSecret(field.value);
      else if (field.type === 'website' || field.type === 'weblogin') uris.push(field.value.trim());
      else if (field.type === 'notes') noteParts.push(field.value.trim());
      else customFields.push(keptField(field));
    }
  }

  const base = {
    notes: joinNotes([...noteParts, totp.extraNote, ...missing.map((fileName) => `Piece jointe absente de l'export : ${fileName}`)]),
    favorite: card.attributes['star'] === 'true',
    ...(tags.length > 0 ? { tags } : {}),
    ...(customFields.length > 0 ? { customFields } : {}),
    ...(attachments.length > 0 ? { attachments } : {}),
  };
  if (isCard) {
    const typed = normalizeTypedFields('card', cardFields);
    return {
      type: 'card',
      name: title || typed['cardholder'] || 'Sans nom',
      username: '',
      password: '',
      uris: [],
      totpSecret: '',
      fields: typed,
      ...base,
    };
  }
  // Ni identifiant, ni mot de passe, ni adresse, ni TOTP : c'est une note securisee.
  const isNote = card.attributes['type'] === 'note' || (username.length === 0 && password.length === 0 && uris.length === 0 && totp.secret.length === 0);
  const host = uris.length > 0 ? serverHost(uris[0]!).replace(/^www\./u, '') : '';
  return {
    type: isNote ? 'note' : 'login',
    name: title || host || username || 'Sans nom',
    username,
    password,
    uris,
    totpSecret: totp.secret,
    ...safeInCloudPasswordChangedAt(card, password),
    ...base,
  };
}

/** Un export SafeInCloud se reconnait a son document `<database>` portant des `card` ou des `label`. */
export function isSafeInCloudXml(text: string): boolean {
  return safeInCloudDatabase(text) !== null;
}

/** Parse un export SafeInCloud XML : chaque fiche hors modeles et corbeille, avec ses etiquettes et pieces jointes. */
export async function parseSafeInCloudXml(text: string): Promise<ImportedEntry[]> {
  const database = safeInCloudDatabase(text);
  if (database === null) throw new Error('export SafeInCloud : structure inattendue');
  const labels = new Map<string, string>();
  for (const label of childrenNamed(database, 'label')) {
    const id = (label.attributes['id'] ?? '').trim();
    const name = (label.attributes['name'] ?? '').trim();
    if (id.length > 0 && name.length > 0) labels.set(id, name);
  }
  const entries: ImportedEntry[] = [];
  for (const card of childrenNamed(database, 'card')) {
    if (card.attributes['template'] === 'true' || card.attributes['deleted'] === 'true') continue;
    entries.push(await safeInCloudEntry(card, labels));
  }
  return entries;
}
